import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, Loader2 } from 'lucide-react';
import { updateJobItem, deleteJobItem, saveJobVariance } from '../../services/api';
import { ActualItemRow, AddItemForm } from './ItemComponents';

export function VarianceModal({ isOpen, onClose, description, varianceData, onRefresh, jobId, hasQuote = true }) {
  const tabs = [
    { id: 'materials', label: 'Materials', type: 1 },
    { id: 'misc', label: 'MISC', type: 2 },
    { id: 'labour', label: 'Labour', type: 3 },
    { id: 'other', label: 'Other', type: 4 },
  ];

  const [editedItems, setEditedItems] = useState({});
  const [saving, setSaving] = useState(false);

  const handleItemChange = (item, qty, cost, total) => {
    setEditedItems(prev => ({ ...prev, [item.quote_item_id || item.job_item_id]: {
      ...item, actual_qty: qty, actual_cost: cost, actual_total: total
    }}));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const allVarianceItems = varianceData?.items || [];
      const descItems = description?.id ? allVarianceItems.filter(i => i.job_description_id === description.id) : allVarianceItems;
      const saveItems = descItems.map(item => {
        const edited = editedItems[item.quote_item_id || item.job_item_id];
        return {
          job_item_id: item.job_item_id || null,
          quote_item_id: item.quote_item_id || null,
          job_description_id: item.job_description_id || null,
          item_name: item.item_name,
          item_type: item.item_type,
          uom: item.actual_uom || item.quoted_uom,
          actual_qty: edited ? edited.actual_qty : item.actual_qty,
          actual_cost: edited ? edited.actual_cost : item.actual_cost,
        };
      });
      await saveJobVariance(jobId, { items: saveItems });
      toast.success('Actual figures saved');
      setEditedItems({});
      onRefresh?.();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const updateMutation = useMutation({
    mutationFn: ({ itemId, data }) => updateJobItem(itemId, data),
    onSuccess: () => { onRefresh?.(); }
  });
  const deleteMutation = useMutation({
    mutationFn: (itemId) => deleteJobItem(itemId),
    onSuccess: () => { toast.success('Item removed'); onRefresh?.(); }
  });

  if (!isOpen || !varianceData) return null;

  const allItems = varianceData.items || [];
  const items = description?.id ? allItems.filter(i => i.job_description_id === description.id) : allItems;

  let liveQuotedTotal = 0;
  let liveActualTotal = 0;
  items.forEach(item => {
    const edited = editedItems[item.quote_item_id || item.job_item_id];
    liveQuotedTotal += item.quoted_total || 0;
    liveActualTotal += edited ? edited.actual_total : item.actual_total;
  });
  const allQuotedTotal = liveQuotedTotal;
  const allActualTotal = liveActualTotal;
  const overallVariance = allQuotedTotal - allActualTotal;
  const profitPercent = allQuotedTotal > 0 ? Math.round((overallVariance / allQuotedTotal) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="variance-modal">
      <div className="bg-white rounded-lg w-full max-w-7xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h2 className="font-semibold text-lg">Quoted Items vs Actual Items</h2>
            {description && <p className="text-sm text-slate-500">{description.job_title}</p>}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2 disabled:opacity-50" data-testid="save-variance-btn">
              {saving && <Loader2 size={14} className="animate-spin" />}
              Save Actuals
            </button>
            <button onClick={onClose} data-testid="variance-close"><X size={20} /></button>
          </div>
        </div>
        {hasQuote && (
          <div className="p-3 bg-yellow-50 border-b text-xs">
            <span className="text-red-600 font-bold">Note:</span> Check/Uncheck the checkbox to show or hide items to the worker(s).
          </div>
        )}
        {!hasQuote && (
          <div className="p-3 bg-blue-50 border-b text-xs text-slate-700">
            <span className="font-bold">Tip:</span> This job has no quote. Use <b>Build Job</b> to set the planned/billed items. Adding an item here records an <b>actual cost only</b> — it will not change the quoted/planned totals.
          </div>
        )}

        <div className="flex-1 overflow-y-auto bg-gray-100 p-4">
          {tabs.map((tab, tabIdx) => {
            const catItems = items.filter(i => i.item_type === tab.type);
            const catQuotedTotal = catItems.reduce((s, i) => s + (i.quoted_total || 0), 0);
            const catActualTotal = catItems.reduce((s, i) => {
              const edited = editedItems[i.quote_item_id || i.job_item_id];
              return s + (edited ? edited.actual_total : (i.actual_qty || 0) * (i.actual_cost || 0));
            }, 0);
            const catVar = catQuotedTotal - catActualTotal;
            const quotedRows = catItems.filter(i => !i.is_additional || i.quoted_total > 0);
            return (
              <div key={tab.id} className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  {tabIdx === 0 && <h5 className="text-center bg-cyan-500 text-white p-2 rounded-t mb-0 font-bold">Quoted</h5>}
                  <div className="bg-white border rounded p-3">
                    <div className="font-bold mb-2">{tab.label}</div>
                    <table className="w-full text-xs">
                      <thead><tr className="border-b text-slate-500">
                        <th className="p-1 w-8"></th><th className="p-1 text-left">Item</th><th className="p-1 text-center">UOM</th><th className="p-1 text-center">Qty</th><th className="p-1 text-right">{hasQuote ? 'Cost' : 'Sell'}</th>
                        <th className="p-1 text-right">Total</th>
                      </tr></thead>
                      <tbody>
                        {quotedRows.length === 0 ? (
                          <tr><td colSpan={6} className="p-3 text-center text-slate-400 text-xs">No quoted items</td></tr>
                        ) : quotedRows.map((item, qIdx) => (
                          <tr key={item.quote_item_id || item.job_item_id || `qr-${qIdx}`} className="border-b hover:bg-slate-50">
                            <td className="p-1 text-center"><input type="checkbox" defaultChecked={item.is_viewable !== false} className="rounded" /></td>
                            <td className="p-1">{item.item_name}</td>
                            <td className="p-1 text-center">{item.quoted_uom}</td>
                            <td className="p-1 text-center">{item.quoted_qty}</td>
                            <td className="p-1 text-right">{(item.quoted_cost || 0).toFixed(2)}</td>
                            <td className="p-1 text-right">{(item.quoted_total || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="font-bold border-t-2">
                          <td colSpan={5} className="p-1">TOTAL</td>
                          <td className="p-1 text-right">{catQuotedTotal.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  {tabIdx === 0 && <h5 className="text-center bg-green-500 text-white p-2 rounded-t mb-0 font-bold">Actual</h5>}
                  <div className="bg-white border rounded p-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-bold">{tab.label}</span>
                      {description?.id && <AddItemForm itemType={tab.type} jobDescriptionId={description.id} onAdded={() => onRefresh?.()} existingItems={catItems} />}
                    </div>
                    <table className="w-full text-xs table-fixed">
                      <thead><tr className="border-b text-slate-500">
                        <th className="p-1" style={{width:'24px'}}></th><th className="p-1 text-left">Item</th><th className="p-1 text-center" style={{width:'48px'}}>UOM</th><th className="p-1 text-center" style={{width:'64px'}}>Qty</th><th className="p-1 text-right" style={{width:'80px'}}>Cost</th><th className="p-1 text-right" style={{width:'80px'}}>Total</th><th className="p-1 text-right" style={{width:'72px'}}>Variance</th>
                      </tr></thead>
                      <tbody>
                        {catItems.length === 0 ? (
                          <tr><td colSpan={7} className="p-3 text-center text-slate-400 text-xs">No items</td></tr>
                        ) : catItems.map((item, idx) => (
                          <ActualItemRow
                            key={item.job_item_id || `q-${idx}`}
                            item={item}
                            onUpdate={(itemId, data) => updateMutation.mutate({ itemId, data })}
                            onDelete={(itemId) => deleteMutation.mutate(itemId)}
                            onChange={handleItemChange}
                          />
                        ))}
                        <tr className="font-bold border-t-2">
                          <td colSpan={5} className="p-1">Total</td>
                          <td className="p-1 text-right">{catActualTotal.toFixed(2)}</td>
                          <td className={`p-1 text-right ${catVar > 0 ? 'text-green-600' : catVar < 0 ? 'text-red-600' : 'text-slate-500'}`}>{catVar.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white border rounded p-4">
              <h5 className="font-bold mb-2">Total Quote: <span className="float-right">$ {allQuotedTotal.toFixed(2)}</span></h5>
              <div className="text-sm text-slate-500">GST 10% <span className="float-right">$ {(allQuotedTotal * 0.10).toFixed(2)}</span></div>
              <div className="text-sm font-bold text-slate-500 mt-1"><span className="float-right">$ {(allQuotedTotal * 1.10).toFixed(2)}</span></div>
            </div>
            <div>
              <div className="bg-white border rounded p-4 mb-3">
                <h5 className="font-bold mb-2">Total Actual: <span className="float-right">$ {allActualTotal.toFixed(2)}</span></h5>
                <div className="text-sm text-slate-500">GST 10% <span className="float-right">$ {(allActualTotal * 0.10).toFixed(2)}</span></div>
                <div className="text-sm font-bold text-slate-500 mt-1"><span className="float-right">$ {(allActualTotal * 1.10).toFixed(2)}</span></div>
              </div>
              <div className={`border rounded p-3 ${allActualTotal > 0 ? (overallVariance >= 0 ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300') : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex justify-between items-center">
                  <span className="font-bold text-sm">Profit:</span>
                  <span className="font-bold text-sm">{allActualTotal > 0 ? `${profitPercent}%` : 'N/A'}</span>
                </div>
                <div className={`flex justify-between items-center mt-1 pt-1 border-t ${overallVariance >= 0 ? 'border-green-200' : 'border-red-200'}`}>
                  <span className="text-xs text-slate-500">{allActualTotal === 0 ? 'No actual costs entered' : 'Quote - Actual'}</span>
                  <span className={`font-bold ${overallVariance >= 0 ? 'text-green-700' : 'text-red-700'}`}>$ {overallVariance.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="p-4 border-t flex justify-end"><button onClick={onClose} className="px-4 py-2 text-sm border rounded">Close</button></div>
      </div>
    </div>
  );
}
